<app-shared-action-bar
  [displayActionSectionBar]="true"
  [saveReady]="hasUnsavedChanges && !saving"
  [undoReady]="hasUnsavedChanges && !saving"
  alternativeBackDestination="/settings"
  (saveRequest)="onSaveRequested()"
  (undoRequest)="onUndoRequested()">
</app-shared-action-bar>

<div class="container workflows-page p-4">
  <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
    <div>
      <p class="text-xs uppercase tracking-wide text-grey-700 mb-1">Events</p>
      <h1 class="text-2xl font-semibold leading-tight">Workflows</h1>
      <p class="text-sm text-grey-800">Create reusable sets of actions you can quickly apply to events.</p>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <div class="lg:col-span-2 card shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-xl font-semibold">Existing workflows</h2>
          @if (errorMessage) {
            <span class="text-danger-base text-sm">{{errorMessage}}</span>
          }
      </div>
      @if (loading) {
        <p class="text-sm text-grey-800">Loading workflows...</p>
      }
      @if (!loading && workflows.length === 0 && !errorMessage) {
        <p class="text-sm text-grey-800">No workflows yet. Create your first workflow to get started.</p>
      }
      @if (!loading && workflows.length > 0) {
        <div class="flex flex-col gap-3">
          @for (wf of workflows; track wf.WorkflowID) {
            <div class="workflow-row border border-grey-100 rounded-lg p-3 bg-white shadow-xs">
                <div class="flex justify-between gap-3">
                  <div class="flex-1">
                  @if (!editedWorkflows[wf.WorkflowID]) {
                    <p class="font-semibold text-grey-900 text-lg">{{wf.Name}}</p>
                    <p class="text-sm text-grey-800 mt-1">{{wf.Description || 'No description provided.'}}</p>
                  }
                  @if (editedWorkflows[wf.WorkflowID]) {
                    <input class="text-box w-full mb-2" type="text" [(ngModel)]="editedWorkflows[wf.WorkflowID].Name" placeholder="Workflow name">
                    <textarea class="text-box w-full" rows="3" [(ngModel)]="editedWorkflows[wf.WorkflowID].Description" placeholder="Description"></textarea>
                  }
                  <p class="text-xs text-grey-600 mt-2">
                    <span class="font-semibold">{{totalActions(wf.WorkflowID)}}</span> {{actionWord(totalActions(wf.WorkflowID))}}
                    <span class="mx-2">•</span>
                    <span class="font-semibold">{{mandatoryActions(wf.WorkflowID)}}</span> mandatory
                    <span class="mx-2">•</span>
                    <span>{{wf.LastUpdated ? ('Updated ' + (wf.LastUpdated | date:'dd MMM yyyy')) : 'Not updated yet'}}</span>
                  </p>
                </div>
                <div class="flex items-center gap-2 workflow-actions-meta">
                  <span class="pill">{{totalActions(wf.WorkflowID)}} {{actionWord(totalActions(wf.WorkflowID))}}</span>
                  <a class="btn-primary btn-compact workflow-view-btn" [routerLink]="['/settings/events/workflows', wf.WorkflowID]">
                    <i class="pi pi-eye"></i>
                    <span class="ml-1">View</span>
                  </a>
                  @if (!editedWorkflows[wf.WorkflowID]) {
                    <button class="btn-inverse btn-compact workflow-action-btn" type="button" (click)="startWorkflowEdit(wf)">
                      <i class="pi pi-pencil"></i>
                    </button>
                    <button class="btn-negative btn-compact workflow-action-btn" type="button" pConfirmPopup (click)="confirmDeleteWorkflow($event, wf.WorkflowID)">
                      <i class="pi pi-trash"></i>
                    </button>
                  }
                  @if (editedWorkflows[wf.WorkflowID]) {
                    <button class="btn-primary btn-compact workflow-action-btn" type="button" (click)="confirmWorkflowEdit(wf.WorkflowID)">
                      <i class="pi pi-check"></i>
                    </button>
                    <button class="btn-inverse btn-compact workflow-action-btn" type="button" (click)="cancelWorkflowEdit(wf.WorkflowID)">
                      <i class="pi pi-times"></i>
                    </button>
                  }
                </div>
              </div>

            </div>
          }
        </div>
      }
    </div>

    <div class="card shadow-sm">
      <h2 class="text-xl font-semibold mb-2">Create workflow</h2>
      <p class="text-sm text-grey-800 mb-3">Name your workflow and describe when to use it. You can add actions after creation.</p>
      <label class="text-sm font-semibold text-grey-900">Workflow name</label>
      <input class="text-box w-full mt-1" type="text" [(ngModel)]="newWorkflowName" placeholder="e.g. Incident follow-up pack">
      <label class="text-sm font-semibold text-grey-900 mt-3">Description</label>
      <textarea class="text-box w-full mt-1" rows="4" [(ngModel)]="newWorkflowDescription" placeholder="Explain what this workflow covers"></textarea>
      @if (formError) {
        <p class="text-danger-base text-sm mt-2">{{formError}}</p>
      }
      <div class="flex justify-end mt-3">
        <button class="btn-primary btn-small" type="button" (click)="createWorkflow()" [disabled]="saving">
          <i class="pi" [ngClass]="saving ? 'pi-spin pi-spinner' : 'pi-plus'"></i>
          <span class="ml-1">{{saving ? 'Creating...' : 'Create Workflow'}}</span>
        </button>
      </div>
    </div>
  </div>
</div>

<p-confirmpopup key="workflowConfirm" styleClass="confirm-popup-danger">
  <ng-template pTemplate="headless" let-confirmation>
    <div class="confirm-popup-wrapper flex items-start gap-3">
      <i class="pi pi-exclamation-triangle text-danger-base text-xl"></i>
      <div class="flex flex-col gap-3 w-full">
        <div>
          <p class="font-semibold">{{confirmation?.header}}</p>
          <p class="text-sm">{{confirmation?.message}}</p>
        </div>
        <div class="flex gap-2 justify-end w-full">
          <button type="button" class="btn-inverse btn-compact btn-small confirm-keep-btn" (click)="onConfirmReject(confirmation)">
            <i class="pi pi-undo mr-1"></i>
            <span>Keep</span>
          </button>
          <button type="button" class="btn-negative btn-compact btn-small confirm-remove-btn" (click)="onConfirmAccept(confirmation)">
            <i class="pi pi-trash mr-1"></i>
            <span>Remove</span>
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</p-confirmpopup>
